import { useState, useEffect } from "react";
import { format } from "date-fns";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar as CalendarIcon, Copy, Check, Loader2, PartyPopper } from "lucide-react";
import { Reveal } from "@/components/motion";
import { api, CLINIC, upiString } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent } from "@/components/ui/dialog";

const to12h = (t) => {
  const [h, m] = t.split(":").map(Number);
  const ap = h >= 12 ? "PM" : "AM";
  const hr = h % 12 === 0 ? 12 : h % 12;
  return `${hr}:${String(m).padStart(2, "0")} ${ap}`;
};

const emptyForm = { patient_name: "", age: "", sex: "", address: "", phone: "" };

export default function Booking() {
  const [form, setForm] = useState(emptyForm);
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState("");
  const [window_, setWindow] = useState("");
  const [paid, setPaid] = useState(false);
  const [copied, setCopied] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (!date) return;
    const ds = format(date, "yyyy-MM-dd");
    setSlotsLoading(true);
    setSelectedSlot("");
    api
      .get(`/slots`, { params: { date: ds } })
      .then((r) => {
        setSlots(r.data.slots);
        setWindow(r.data.window);
      })
      .catch(() => toast.error("Could not load time slots"))
      .finally(() => setSlotsLoading(false));
  }, [date]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const copyUpi = () => {
    navigator.clipboard.writeText(CLINIC.upiId);
    setCopied(true);
    toast.success("UPI ID copied");
    setTimeout(() => setCopied(false), 1800);
  };

  const validate = () => {
    if (!form.patient_name.trim()) return "Please enter patient name";
    if (!form.age || Number(form.age) < 0 || Number(form.age) > 120) return "Please enter a valid age";
    if (!form.sex) return "Please select sex";
    if (!form.address.trim()) return "Please enter address";
    if (!date) return "Please select an appointment date";
    if (!selectedSlot) return "Please select a time slot";
    if (!paid) return "Please confirm the ₹300 UPI payment";
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) return toast.error(err);
    setSubmitting(true);
    try {
      const payload = {
        patient_name: form.patient_name.trim(),
        age: Number(form.age),
        sex: form.sex,
        address: form.address.trim(),
        phone: form.phone.trim() || null,
        date: format(date, "yyyy-MM-dd"),
        time_slot: selectedSlot,
      };
      const { data } = await api.post("/appointments", payload);
      setSuccess(data);
      setForm(emptyForm);
      setSelectedSlot("");
      setPaid(false);
      // refresh slots
      const ds = format(date, "yyyy-MM-dd");
      const r = await api.get(`/slots`, { params: { date: ds } });
      setSlots(r.data.slots);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Booking failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full px-4 py-3 rounded-xl border border-border bg-white text-[var(--ink)] placeholder:text-slate-400 focus:outline-none focus:border-[var(--teal)] focus:ring-2 focus:ring-[var(--teal)]/15 transition";

  return (
    <section id="booking" className="py-24 sm:py-32 bg-[var(--teal-dark)] grain relative">
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <p className="text-[var(--teal-light)] text-sm tracking-[0.25em] uppercase mb-4">Book your visit</p>
          <h2 className="font-display text-4xl sm:text-5xl md:text-6xl tracking-tight text-white max-w-2xl">
            Schedule your appointment.
          </h2>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Form */}
          <Reveal className="lg:col-span-7" delay={0.08}>
            <div className="rounded-3xl bg-white p-7 sm:p-10 shadow-[0_30px_60px_rgb(0,0,0,0.2)]">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-[var(--ink)] mb-2">Patient Name</label>
                  <input data-testid="input-name" className={inputCls} value={form.patient_name} onChange={set("patient_name")} placeholder="Full name" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--ink)] mb-2">Age</label>
                  <input data-testid="input-age" type="number" min="0" max="120" className={inputCls} value={form.age} onChange={set("age")} placeholder="Years" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--ink)] mb-2">Sex</label>
                  <div className="flex gap-2">
                    {["Male", "Female", "Other"].map((s) => (
                      <button
                        key={s}
                        type="button"
                        data-testid={`sex-${s.toLowerCase()}`}
                        onClick={() => setForm({ ...form, sex: s })}
                        className={`flex-1 px-3 py-3 rounded-xl border text-sm font-medium transition ${
                          form.sex === s ? "bg-[var(--teal)] text-white border-[var(--teal)]" : "bg-white border-border text-[var(--ink-soft)] hover:border-[var(--teal)]/40"
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-[var(--ink)] mb-2">Address</label>
                  <textarea data-testid="input-address" rows={2} className={inputCls} value={form.address} onChange={set("address")} placeholder="Residential address" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-[var(--ink)] mb-2">Phone (optional)</label>
                  <input data-testid="input-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="Contact number" />
                </div>

                {/* Date */}
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-[var(--ink)] mb-2">Date of Appointment</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button data-testid="date-trigger" className={`${inputCls} flex items-center justify-between text-left`}>
                        <span className={date ? "text-[var(--ink)]" : "text-slate-400"}>
                          {date ? format(date, "EEEE, dd MMM yyyy") : "Select a date"}
                        </span>
                        <CalendarIcon size={18} className="text-[var(--teal)]" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* Slots */}
              {date && (
                <div className="mt-6">
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-[var(--ink)]">Time Slot</label>
                    {window_ && <span className="text-xs text-[var(--teal)] font-medium">{window_}</span>}
                  </div>
                  {slotsLoading ? (
                    <div className="flex items-center gap-2 text-[var(--ink-soft)] py-6"><Loader2 className="animate-spin" size={18} /> Loading slots…</div>
                  ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 max-h-56 overflow-y-auto pr-1" data-testid="slots-grid">
                      {slots.map((s) => (
                        <button
                          key={s.time}
                          type="button"
                          disabled={!s.available}
                          data-testid={`slot-${s.time}`}
                          onClick={() => setSelectedSlot(s.time)}
                          className={`px-2 py-2.5 rounded-lg text-sm font-medium border transition ${
                            !s.available
                              ? "bg-slate-100 text-slate-300 border-transparent cursor-not-allowed line-through"
                              : selectedSlot === s.time
                              ? "bg-[var(--teal)] text-white border-[var(--teal)]"
                              : "bg-white border-border text-[var(--ink)] hover:border-[var(--teal)]"
                          }`}
                        >
                          {to12h(s.time)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </Reveal>

          {/* Payment */}
          <Reveal className="lg:col-span-5" delay={0.16}>
            <div className="rounded-3xl bg-white p-7 sm:p-10 shadow-[0_30px_60px_rgb(0,0,0,0.2)] h-full flex flex-col">
              <div className="flex items-baseline justify-between">
                <h3 className="font-display text-3xl text-[var(--ink)]">Consultation Fee</h3>
                <span className="font-display text-4xl text-[var(--teal)]">₹300</span>
              </div>
              <p className="mt-2 text-sm text-[var(--ink-soft)]">Scan the QR with any UPI app to pay, then confirm below.</p>

              <div className="mt-6 self-center rounded-2xl border border-border p-4 bg-white" data-testid="upi-qr">
                <QRCodeCanvas value={upiString()} size={196} level="M" fgColor="#0d7a7a" includeMargin={false} />
              </div>

              <button data-testid="copy-upi-btn" onClick={copyUpi} className="mt-5 flex items-center justify-center gap-2 w-full px-4 py-3 rounded-xl bg-[var(--teal-light)] text-[var(--teal-dark)] font-medium hover:bg-[var(--teal-light)]/70 transition">
                {copied ? <Check size={16} /> : <Copy size={16} />} {CLINIC.upiId}
              </button>

              <label className="mt-5 flex items-start gap-3 cursor-pointer select-none">
                <input data-testid="paid-checkbox" type="checkbox" checked={paid} onChange={(e) => setPaid(e.target.checked)} className="mt-1 w-5 h-5 accent-[var(--teal)]" />
                <span className="text-sm text-[var(--ink-soft)]">I have completed the ₹300 payment via UPI.</span>
              </label>

              <button
                data-testid="submit-booking-btn"
                onClick={submit}
                disabled={submitting}
                className="mt-6 w-full px-6 py-4 rounded-full bg-[var(--teal)] text-white font-semibold hover:bg-[var(--teal-dark)] transition disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {submitting ? <><Loader2 className="animate-spin" size={18} /> Booking…</> : "Confirm Appointment"}
              </button>
            </div>
          </Reveal>
        </div>
      </div>

      {/* Success dialog */}
      <Dialog open={!!success} onOpenChange={(o) => !o && setSuccess(null)}>
        <DialogContent className="max-w-md rounded-3xl" data-testid="booking-success">
          <div className="text-center py-4">
            <div className="mx-auto w-16 h-16 rounded-full bg-[var(--teal-light)] flex items-center justify-center text-[var(--teal)] mb-5">
              <PartyPopper size={30} />
            </div>
            <h3 className="font-display text-3xl text-[var(--ink)]">Appointment Confirmed</h3>
            <AnimatePresence>
              {success && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-4 text-[var(--ink-soft)]">
                  <p className="font-medium text-[var(--ink)]">{success.patient_name}</p>
                  <p className="mt-1">{format(new Date(success.date), "EEEE, dd MMM yyyy")} · {to12h(success.time_slot)}</p>
                  <p className="mt-3 text-sm">We look forward to seeing you at Shah Eye Care Centre.</p>
                </motion.div>
              )}
            </AnimatePresence>
            <button data-testid="success-close-btn" onClick={() => setSuccess(null)} className="mt-6 px-8 py-3 rounded-full bg-[var(--teal)] text-white font-semibold">
              Done
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
